import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import { PageHeading } from "@/components/Layout/Parts";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import Text from "@/components/Text";
import routes from "@/utils/routes";
import Button from "@/components/Button";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { ViewGuideResource } from "@/types/resources";
import { GENERAL_GUIDE_KEY } from "@/components/VideoGuideModal";
import ContentSpinner from "@/components/ContentSpinner";

type FormDataT = {
  variations: Array<{
    title: string;
    link: string;
    description: string;
    key: string;
  }>;
};

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px 24px 32px;
  margin-bottom: 24px;
`;

const Title = styled(Text)`
  margin-bottom: 24px;
`;

const StyledButton = styled(Button)`
  padding: 0 74px;
`;

const ButtonHolder = styled.div`
  text-align: center;
  margin-top: 32px;
`;

const KeyInput = styled(Input)`
  display: none;
`;

const ViewGuideSettings = () => {
  const queryClient = useQueryClient();

  const { data: viewGuides, isLoading } = useQuery<ViewGuideResource[]>(
    ["view_guides"],
    () => api.get<never, ViewGuideResource[]>(`/v1/view-guides`),
    {
      refetchOnMount: false,
      staleTime: 120 * 1000,
    }
  );

  const methods = useForm<FormDataT>({});

  const {
    register,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const defaultValues = viewGuides?.reduce<{
    [key: string]: ViewGuideResource;
  }>((previousValue, currentValue) => {
    return {
      ...previousValue,
      [currentValue.key]: currentValue,
    };
  }, {});

  const alerts = useAlerts();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { error } = await api.post<typeof data, { error?: string }>(
        "/v1/view-guides",
        data
      );

      if (error) {
        return alerts.error(error);
      }

      queryClient.refetchQueries(["view_guides"], {
        exact: true,
      });

      alerts.success(`View guides tests have been successfully saved`);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
    }
  });

  const filteredRoutes = routes.filter((route) => route.title);

  return (
    <>
      <PageHeading>View Guide Settings</PageHeading>
      {isLoading ? (
        <ContentSpinner />
      ) : (
        <form onSubmit={onSubmit} autoComplete="off">
          {filteredRoutes.map((route, index) => (
            <Card key={route.path}>
              <Title $type="caption">{route.title}</Title>
              <FormLayout>
                <div>
                  <Label htmlFor={`variationLinkInput-${index}`}>
                    Loom link
                  </Label>
                  <Input
                    id={`variationLinkInput-${index}`}
                    placeholder="Add link"
                    defaultValue={
                      defaultValues?.[route.path as keyof typeof defaultValues]
                        ?.link ?? ""
                    }
                    {...register(`variations.${index}.link`)}
                  />
                </div>
                <KeyInput
                  id={`variationKeyInput-${index}`}
                  hidden
                  defaultValue={route.path}
                  {...register(`variations.${index}.key`)}
                />
                <div>
                  <Label htmlFor={`variationTitleInput-${index}`}>Title</Label>
                  <Input
                    id={`variationTitleInput-${index}`}
                    placeholder="Add title"
                    defaultValue={
                      defaultValues?.[route.path as keyof typeof defaultValues]
                        ?.title ?? ""
                    }
                    {...register(`variations.${index}.title`)}
                  />
                </div>
                <Larger>
                  <Label htmlFor={`variationDescriptionInput-${index}`}>
                    Description
                  </Label>
                  <Textarea
                    id={`variationDescriptionInput-${index}`}
                    placeholder="Add description"
                    defaultValue={
                      defaultValues?.[route.path as keyof typeof defaultValues]
                        ?.description ?? ""
                    }
                    {...register(`variations.${index}.description`)}
                  />
                </Larger>
              </FormLayout>
            </Card>
          ))}
          <Card>
            <Title $type="caption">General guide list</Title>
            <FormLayout>
              <Larger>
                <Label htmlFor={`variationLinkInput-${filteredRoutes.length}`}>
                  Loom link
                </Label>
                <Input
                  id={`variationLinkInput-${filteredRoutes.length}`}
                  placeholder="Add link"
                  defaultValue={defaultValues?.[GENERAL_GUIDE_KEY]?.link || ""}
                  {...register(`variations.${filteredRoutes.length}.link`)}
                />
                <KeyInput
                  id={`variationKeyInput-${filteredRoutes.length}`}
                  hidden
                  defaultValue={GENERAL_GUIDE_KEY}
                  {...register(`variations.${filteredRoutes.length}.key`)}
                />
              </Larger>
            </FormLayout>
          </Card>
          <ButtonHolder>
            <StyledButton disabled={isSubmitting} loading={isSubmitting}>
              Save
            </StyledButton>
          </ButtonHolder>
        </form>
      )}
    </>
  );
};

export default ViewGuideSettings;
